<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-1">
      <button
        data-test="open-claim"
        type="button"
        class="btn-icon btn-icon-default"
      >
        <svg
          class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>

      <button
        v-if="canEditClaim"
        type="button"
        class="btn-icon btn-icon-default"
        @click.stop="createCost"
      >
        <svg
          class="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
      </button>

      <span class="capitalize">
        {{ title.replace('_', ' ') }}
      </span>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td
      class="bg-green-200/50"
      :class="{ 'text-red-600': totalCosts.variance !== 0 }"
    >
      {{ australianCurrency(totalCosts.variance) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget - totalCosts.cumulative) }}
    </td>
  </tr>

  <template v-if="open">
    <VariationOrderCostRow
      v-for="cost in uncategorizedCosts"
      :key="cost.id"
      :claim="$props.claim"
      :variation-order-cost="cost"
      :variation-order="cost.variation_order"
      :show-percentages="$props.showPercentages"
    />

    <VariationOrderCategoryRow
      v-for="group in categorizedGroups"
      :key="group.key"
      :group="group"
      :variation-orders="group.variationOrders"
      :costs="group.costs"
      :claim="$props.claim"
      :show-percentages="$props.showPercentages"
    />

    <tr
      v-if="costs.length === 0"
      class="bg-gray-50"
    >
      <td
        colspan="100%"
        class="text-center p-4 text-gray-500"
      >
        <p>
          No {{ title.replace('_', ' ') }}s found for this claim
        </p>

        <button
          v-if="canEditClaim"
          type="button"
          class="btn btn--secondary-blue mt-2"
          @click="createCost"
        >
          Attach {{ title.replace('_', ' ') }}
        </button>
      </td>
    </tr>

    <VariationOrderCostModal
      v-if="show"
      :claim="claim"
      :variation-order-cost="selectedCost"
      :type="variationOrderType"
      @close="show = false"
    />
  </template>
</template>

<script setup lang="ts">
import { useSignedData } from '@/composables/useSignedData';
import { australianCurrency, australianNumber } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import { VariationOrderType } from '@/models/VariationOrder';
import VariationOrderCost from '@/models/VariationOrderCost';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { computed, ref } from 'vue';
import VariationOrderCategoryRow from './VariationOrderCategoryRow.vue';
import VariationOrderCostModal from './VariationOrderCostModal.vue';
import VariationOrderCostRow from './VariationOrderCostRow.vue';

const props = defineProps<{
  costs: VariationOrderCost[];
  claim: Claim;
  title: string;
  variationOrderType: ObjectValues<typeof VariationOrderType>;
  showPercentages?: boolean;
}>();

const variationOrderStore = useVariationOrdersStore();
const categoriesStore = useVariationOrderCategoriesStore();

const variationOrders = computed(() => {
  return variationOrderStore.models.where('type', props.variationOrderType).whereHas(
    'costs',
    (query) => {
      query.where('claim_id', props.claim.id);
    },
  )
    .with(
      'costs',
      (query) => {
        query.where('claim_id', props.claim.id);
      },
    )
    .get();
});

const totalCosts = computed(() => {
  const totals = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
  };

  variationOrders.value.forEach((variationOrder) => {
    totals.budget += variationOrder.budget;
    totals.previous += variationOrder.previous(props.claim);
    const variationOrderCost = variationOrder.costs[0];

    if(variationOrderCost) {
      totals.currentClaim += variationOrderCost.current_claim;
      totals.currentCertified += variationOrderCost.current_certified;
      totals.variance += variationOrderCost.current_claim - variationOrderCost.current_certified;
    }
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});

const categorizedGroups = computed(() => {
  const projectId = props.claim.project_id;

  const categories = categoriesStore.models
    .where('project_id', projectId)
    .whereNull('package_id')
    .orderBy('sort_order')
    .get();

  const groups: {
    key: string;
    label: string;
    costs: VariationOrderCost[];
    variationOrders: typeof variationOrders.value;
  }[] = [];

  categories.forEach((category) => {
    const orders = variationOrders.value.filter((vo) => vo.variation_order_category_id === category.id);
    const costs = props.costs.filter((cost) => cost.variation_order?.variation_order_category_id === category.id);

    if(!orders.length && !costs.length) {
      return;
    }

    groups.push({
      key: `category-${category.id}`,
      label: category.description,
      costs: costs.length ?
        costs :
        props.costs.filter((cost) => orders.some((vo) => vo.id === cost.variation_order_id)),

      variationOrders: orders,
    });
  });

  return groups;
});

const uncategorizedCosts = computed(() => {
  return props.costs.filter((cost) => !cost.variation_order?.variation_order_category_id);
});

const open = ref(false);
const show = ref(false);
const selectedCost = ref<VariationOrderCost | undefined>();

const createCost = () => {
  selectedCost.value = undefined;
  show.value = true;
  open.value = true;
};

const signedParams = useSignedData();

const canEditClaim = computed(() => {
  return signedParams.meta.edit_claim &&
    (props.claim.status === ClaimStatus.contractor_claim ||
      props.claim.status === ClaimStatus.draft_contractor);
});
</script>

<style scoped></style>
